import '@/Resource/css/main.scss';
import { ColorSchemeScript, createTheme, DEFAULT_THEME, MantineProvider } from '@mantine/core';
import { GoogleTagManager } from '@next/third-parties/google';
import type { NextComponentType, NextPageContext } from 'next';
import type { AppContext as AppNextContext, AppProps } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import type { ReactNode } from 'react';
import React from 'react';
import { Provider } from 'react-redux';
import { CompleteProfileModal } from '@/Module/Auth/Component';
import { ApplicationContext, ApplicationSnackbarProvider } from '@/Module/Core/Component';
import ApplicationLayout from '@/Module/Core/Component/ApplicationLayout/ApplicationLayout';
import { APPLICATION_LINK, APPLICATION_TITLE } from '@/Module/Core/Constant';
import { getAppInitialData, getDeviceTypeByUserAgent, isBrowser } from '@/Module/Core/Helper';
import { useDeviceType } from '@/Module/Core/Hook';
import type { IPage } from '@/Module/Core/Model/DataModel';
import { DeviceType, LayoutType } from '@/Module/Core/Model/Enum';
import { coreSetDeviceTypeAction } from '@/Module/Core/Store/Slice/CoreSlice';
import { SubscriptionPopup, Transition } from '@/Module/DefaultLayout/Component';
import { BrowserCheck, InstallPWA } from '@/Module/HomePage/Component';
import { Wrapper } from '@/Module/Store/Component';
export interface IApplicationProps extends AppProps {
  userAgentString: string;
  Component: NextComponentType<NextPageContext, any, any> & IPage;
}
const Application = ({
  userAgentString,
  Component,
  ...rest
}: IApplicationProps): ReactNode => {
  const {
    store,
    props
  } = Wrapper.useWrappedStore(rest);
  const {
    isLoggedIn
  } = store.getState().auth;
  const deviceType = useDeviceType();
  const theme = createTheme({
    ...DEFAULT_THEME,
    fontFamily: 'pelak'
  });
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
        <title>{APPLICATION_TITLE}</title>
        {APPLICATION_LINK.includes('ino.school') ? <meta name="robots" content="index, follow" /> : <meta name="robots" content="noindex, nofollow" />}
        <meta name="viewport" content={`width=device-width, initial-scale=${deviceType === DeviceType.MOBILE ? 0.5 : 1.0} ${!isLoggedIn ? '' : ', maximum-scale=1.0, user-scalable=0'}`} data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <link rel="apple-touch-icon" sizes="180x180" href="/assets/favicons/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/assets/favicons/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/assets/favicons/favicon-16x16.png" />
        <link rel="shortcut icon" href="/assets/favicons/favicon.ico" />
        <link rel="mask-icon" href="/assets/favicons/icon512_maskable.png" color="#FFFFFF" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="msapplication-TileColor" content="#ffffff" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="msapplication-config" content="/assets/favicons/browserconfig.xml" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="theme-color" content="#ffffff" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <ColorSchemeScript forceColorScheme="light" data-sentry-element="ColorSchemeScript" data-sentry-source-file="_app.tsx" />
      </Head>
      <ApplicationContext userAgent={userAgentString} data-sentry-element="ApplicationContext" data-sentry-source-file="_app.tsx">
        <Provider store={store} data-sentry-element="Provider" data-sentry-source-file="_app.tsx">
          <MantineProvider theme={theme} forceColorScheme="light" data-sentry-element="MantineProvider" data-sentry-source-file="_app.tsx">
            <ApplicationSnackbarProvider data-sentry-element="ApplicationSnackbarProvider" data-sentry-source-file="_app.tsx">
              <ApplicationLayout layout={Component?.layout || LayoutType.DEFAULT} data-sentry-element="ApplicationLayout" data-sentry-source-file="_app.tsx">
                <GoogleTagManager gtmId={APPLICATION_LINK.includes('ino.school') ? 'GTM-5N826FKS' : 'GTM-5PMDWZSB'} data-sentry-element="GoogleTagManager" data-sentry-source-file="_app.tsx" />
                <Transition data-sentry-element="Transition" data-sentry-source-file="_app.tsx" />
                <Component {...props.pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                <SubscriptionPopup data-sentry-element="SubscriptionPopup" data-sentry-source-file="_app.tsx" />
                <InstallPWA data-sentry-element="InstallPWA" data-sentry-source-file="_app.tsx" />
                <BrowserCheck data-sentry-element="BrowserCheck" data-sentry-source-file="_app.tsx" />
                <CompleteProfileModal data-sentry-element="CompleteProfileModal" data-sentry-source-file="_app.tsx" />
              </ApplicationLayout>
            </ApplicationSnackbarProvider>
          </MantineProvider>
        </Provider>
      </ApplicationContext>
    </>;
};
Application.getInitialProps = Wrapper.getInitialAppProps(store => async (context: AppNextContext) => {
  if (!isBrowser()) {
    const deviceType = getDeviceTypeByUserAgent(context?.ctx?.req?.headers?.['user-agent'] ?? '');
    store.dispatch(coreSetDeviceTypeAction(deviceType));
  }
  await getAppInitialData(store, context);
  const pageProps = await App.getInitialProps(context);
  return {
    userAgentString: context?.ctx?.req?.headers?.['user-agent'] ?? '',
    ...pageProps
  };
});
export default Application;